'use client'

import {
    createPlanTier,
    getPlanTier,
    updatePlanTier,
} from '@/lib/api/plan-tiers/plan-tiers-api'
import {
    getSystemModules,
    SystemModule,
} from '@/lib/api/system-modules/system-modules-api'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdExpandLess,
    MdExpandMore,
    MdLayers,
    MdSave,
    MdWidgets,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

interface TierForm {
    name: string
}

export default function PlanTierFormPage() {
    const { id } = useParams()
    const isEdit = !!id
    const navigate = useNavigate()

    const [form, setForm] = useState<TierForm>({
        name: '',
    })
    const [allModules, setAllModules] = useState<SystemModule[]>([])
    const [selectedModuleIds, setSelectedModuleIds] = useState<Set<number>>(new Set())
    const [tierResources, setTierResources] = useState<Record<string, string[]>>({})
    const [expandedModules, setExpandedModules] = useState<Set<number>>(new Set())
    const [loading, setLoading] = useState(false)
    const [modulesLoading, setModulesLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        loadModules()
        if (isEdit && id) {
            loadTier(parseInt(id, 10))
        }
    }, [id])

    const loadModules = async () => {
        try {
            const response = await getSystemModules()
            if (response.success && response.result) {
                setAllModules(response.result)
            }
        } catch {
            // silently fail
        } finally {
            setModulesLoading(false)
        }
    }

    const loadTier = async (tierId: number) => {
        setLoading(true)
        try {
            const response = await getPlanTier(tierId)
            if (response.success && response.result) {
                setForm({
                    name: response.result.name,
                })
                setTierResources(
                    (response.result.resources as Record<string, string[]>) || {},
                )

                // Módulos do tier vêm na própria resposta (relação M:N).
                const ids = new Set(
                    (response.result.modules ?? []).map(m => m.id),
                )
                setSelectedModuleIds(ids)
            } else {
                toast.error('Erro ao carregar tier')
                navigate('/plan-tiers')
            }
        } catch {
            toast.error('Erro ao carregar tier')
            navigate('/plan-tiers')
        } finally {
            setLoading(false)
        }
    }

    const toggleModule = (mod: SystemModule) => {
        setSelectedModuleIds(prev => {
            const next = new Set(prev)
            if (next.has(mod.id)) {
                next.delete(mod.id)
                // Remover recursos ao desmarcar
                setTierResources(r => {
                    const copy = { ...r }
                    delete copy[mod.key]
                    return copy
                })
                setExpandedModules(e => {
                    const copy = new Set(e)
                    copy.delete(mod.id)
                    return copy
                })
            } else {
                next.add(mod.id)
                // Padrão: todos os recursos ([] = todos)
            }
            return next
        })
    }

    const toggleExpand = (moduleId: number) => {
        setExpandedModules(prev => {
            const next = new Set(prev)
            if (next.has(moduleId)) next.delete(moduleId)
            else next.add(moduleId)
            return next
        })
    }

    const isResourceSelected = (moduleKey: string, resourceKey: string): boolean => {
        const resources = tierResources[moduleKey]
        if (resources === undefined) return true // sem config = todos
        if (resources.length === 0) return true // [] = todos
        return resources.includes(resourceKey)
    }

    const toggleResource = (moduleKey: string, resourceKey: string, mod: SystemModule) => {
        setTierResources(prev => {
            const next = { ...prev }
            const current = next[moduleKey]

            if (current === undefined || current.length === 0) {
                // Todos selecionados -> desmarcar um = listar todos menos esse
                next[moduleKey] = mod.resources
                    .map(r => r.key)
                    .filter(k => k !== resourceKey)
            } else if (current.includes(resourceKey)) {
                const filtered = current.filter(k => k !== resourceKey)
                if (filtered.length === 0) {
                    // Nenhum recurso -> não permitir (mínimo 1)
                    toast.warning('Selecione pelo menos um recurso ou desmarque o módulo')
                    return prev
                }
                next[moduleKey] = filtered
            } else {
                const updated = [...current, resourceKey]
                // Se selecionou todos, remover override (volta ao padrão)
                if (updated.length === mod.resources.length) {
                    delete next[moduleKey]
                } else {
                    next[moduleKey] = updated
                }
            }
            return next
        })
    }

    const getResourceCount = (mod: SystemModule): number => {
        const resources = tierResources[mod.key]
        if (resources === undefined || resources.length === 0) return mod.resources.length
        return resources.length
    }

    const validate = (): boolean => {
        if (!form.name.trim()) {
            toast.error('O nome do tier é obrigatório')
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) return

        setSaving(true)
        try {
            // moduleIds compõem o tier via M:N — o backend faz o set atômico.
            const payload = {
                name: form.name.trim(),
                resources: tierResources,
                moduleIds: [...selectedModuleIds],
            }

            if (isEdit && id) {
                const response = await updatePlanTier(parseInt(id, 10), payload)
                if (!response.success) {
                    toast.error(response.error || 'Erro ao salvar tier')
                    return
                }
            } else {
                const response = await createPlanTier(payload)
                if (!response.success || !response.result) {
                    toast.error(response.error || 'Erro ao salvar tier')
                    return
                }
            }

            toast.success(
                isEdit
                    ? 'Tier atualizado com sucesso'
                    : 'Tier criado com sucesso',
            )
            navigate('/plan-tiers')
        } catch {
            toast.error('Erro ao salvar tier')
        } finally {
            setSaving(false)
        }
    }

    // M:N: todos os módulos podem entrar em qualquer tier.
    const availableModules = allModules

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando tier...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/plan-tiers')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-lg" />
                </button>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                    <MdLayers className="text-3xl text-white" />
                </div>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                        {isEdit ? 'Editar Tier' : 'Novo Tier'}
                    </h1>
                    <p className="text-sm text-base-content/60 mt-1">
                        {isEdit
                            ? 'Atualize as informações do tier e seus módulos'
                            : 'Defina o nome, nível e os módulos deste tier'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Dados do Tier */}
                <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-bold text-base-content mb-4">
                        Dados do Tier
                    </h2>

                    <div className="form-control">
                        <label className="label">
                            <span className="label-text font-medium">
                                Nome *
                            </span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered"
                            placeholder="Ex: Básico, Padrão, Premium"
                            value={form.name}
                            onChange={e =>
                                setForm(prev => ({
                                    ...prev,
                                    name: e.target.value,
                                }))
                            }
                            maxLength={100}
                        />
                        <label className="label">
                            <span className="label-text-alt text-base-content/50">
                                Nome único para identificar o tier
                            </span>
                        </label>
                    </div>
                </div>

                {/* Módulos e Recursos do Tier */}
                <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-lg font-bold text-base-content flex items-center gap-2">
                                <MdWidgets className="text-secondary" />
                                Módulos e Recursos
                                {modulesLoading && (
                                    <span className="loading loading-spinner loading-xs text-secondary"></span>
                                )}
                            </h2>
                            <p className="text-sm text-base-content/60 mt-1">
                                Selecione os módulos e configure quais recursos estarão disponíveis
                            </p>
                        </div>
                        <span className="badge badge-secondary">
                            {selectedModuleIds.size} módulo(s)
                        </span>
                    </div>

                    {availableModules.length === 0 && !modulesLoading ? (
                        <div className="text-center py-8 text-base-content/50 border-2 border-dashed border-base-300 rounded-xl">
                            <p>Nenhum módulo cadastrado no sistema</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {availableModules.map(mod => {
                                const isSelected = selectedModuleIds.has(mod.id)
                                const isExpanded = expandedModules.has(mod.id)
                                const resourceCount = isSelected ? getResourceCount(mod) : 0
                                const hasResourceOverride = tierResources[mod.key] !== undefined

                                return (
                                    <div
                                        key={mod.id}
                                        className={`rounded-lg border transition-all ${
                                            isSelected
                                                ? hasResourceOverride
                                                    ? 'border-warning/50 bg-warning/5'
                                                    : 'border-secondary/50 bg-secondary/5'
                                                : 'border-base-300'
                                        }`}
                                    >
                                        <div
                                            className="flex items-center gap-3 p-3 cursor-pointer"
                                            onClick={() => {
                                                if (!isSelected) {
                                                    toggleModule(mod)
                                                    if (mod.resources.length > 0) {
                                                        setExpandedModules(prev => new Set(prev).add(mod.id))
                                                    }
                                                } else if (mod.resources.length > 0) {
                                                    toggleExpand(mod.id)
                                                }
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm checkbox-secondary"
                                                checked={isSelected}
                                                onChange={e => {
                                                    e.stopPropagation()
                                                    toggleModule(mod)
                                                    if (!isSelected && mod.resources.length > 0) {
                                                        setExpandedModules(prev => new Set(prev).add(mod.id))
                                                    }
                                                }}
                                            />
                                            <div className="flex-1 flex items-center justify-between">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-semibold text-base-content">
                                                        {mod.label}
                                                    </span>
                                                    <span className="badge badge-xs badge-ghost font-mono">
                                                        {mod.key}
                                                    </span>
                                                    {isSelected && mod.resources.length > 0 && (
                                                        <span className={`badge badge-xs ${hasResourceOverride ? 'badge-warning' : 'badge-secondary'}`}>
                                                            {resourceCount}/{mod.resources.length}
                                                        </span>
                                                    )}
                                                    {isSelected && hasResourceOverride && (
                                                        <span className="badge badge-xs badge-warning badge-outline">
                                                            personalizado
                                                        </span>
                                                    )}
                                                </div>
                                                {isSelected && mod.resources.length > 0 && (
                                                    isExpanded ? (
                                                        <MdExpandLess className="text-lg text-base-content/50" />
                                                    ) : (
                                                        <MdExpandMore className="text-lg text-base-content/50" />
                                                    )
                                                )}
                                            </div>
                                        </div>

                                        {isExpanded && isSelected && mod.resources.length > 0 && (
                                            <div className="px-3 pb-3 pt-0">
                                                <div className="ml-8 grid grid-cols-2 md:grid-cols-3 gap-2">
                                                    {mod.resources.map(res => (
                                                        <label
                                                            key={res.key}
                                                            className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-all text-sm ${
                                                                isResourceSelected(mod.key, res.key)
                                                                    ? 'bg-secondary/10'
                                                                    : 'hover:bg-base-200'
                                                            }`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className="checkbox checkbox-xs checkbox-secondary"
                                                                checked={isResourceSelected(mod.key, res.key)}
                                                                onChange={() => toggleResource(mod.key, res.key, mod)}
                                                            />
                                                            <span>{res.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}

                </div>

                {/* Info */}
                <div className="bg-info/10 border border-info/20 rounded-xl p-4">
                    <p className="text-sm text-info-content/80">
                        <strong>Como funciona:</strong> Cada tier é um pacote independente de módulos. Um plano com este
                        tier terá acesso exatamente aos módulos selecionados aqui, com os recursos configurados. O mesmo
                        módulo pode pertencer a vários tiers, e o plano ainda pode fazer overrides adicionais.
                    </p>
                </div>

                {/* Ações */}
                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/plan-tiers')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-lg" />
                        )}
                        {isEdit ? 'Salvar Alterações' : 'Criar Tier'}
                    </button>
                </div>
            </form>
        </div>
    )
}
